/**
 * Guitar Preset Selector Component
 * Quick-access preset browser with categories and user presets
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DropdownPortal } from '../../ui/dropdown-portal';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  ChevronDown,
  Search,
  Star,
  Save,
  Trash2,
  Check,
  X,
  Zap,
  Guitar,
  Music,
  Sparkles,
} from 'lucide-react';
import {
  FACTORY_PRESETS,
  PRESET_CATEGORIES,
  getPresetsByCategory,
  applyPresetToTrack,
  createUserPreset,
} from './GuitarPresets';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compact preset card for quick selection
const PresetCard = ({ preset, isSelected, onSelect, onDelete, compact = false }) => {
  const colorMap = {
    orange: 'border-orange-500/50 bg-orange-500/10 hover:bg-orange-500/20',
    red: 'border-red-500/50 bg-red-500/10 hover:bg-red-500/20',
    cyan: 'border-cyan-500/50 bg-cyan-500/10 hover:bg-cyan-500/20',
    blue: 'border-blue-500/50 bg-blue-500/10 hover:bg-blue-500/20',
    pink: 'border-pink-500/50 bg-pink-500/10 hover:bg-pink-500/20',
    violet: 'border-violet-500/50 bg-violet-500/10 hover:bg-violet-500/20',
    amber: 'border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20',
    emerald: 'border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20',
    slate: 'border-slate-500/50 bg-slate-500/10 hover:bg-slate-500/20',
  };

  const selectedColor = {
    orange: 'border-orange-400 bg-orange-500/30 ring-1 ring-orange-400/50',
    red: 'border-red-400 bg-red-500/30 ring-1 ring-red-400/50',
    cyan: 'border-cyan-400 bg-cyan-500/30 ring-1 ring-cyan-400/50',
    blue: 'border-blue-400 bg-blue-500/30 ring-1 ring-blue-400/50',
    pink: 'border-pink-400 bg-pink-500/30 ring-1 ring-pink-400/50',
    violet: 'border-violet-400 bg-violet-500/30 ring-1 ring-violet-400/50',
    amber: 'border-amber-400 bg-amber-500/30 ring-1 ring-amber-400/50',
    emerald: 'border-emerald-400 bg-emerald-500/30 ring-1 ring-emerald-400/50',
    slate: 'border-slate-400 bg-slate-500/30 ring-1 ring-slate-400/50',
  };

  if (compact) {
    return (
      <button
        onClick={() => onSelect(preset)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
          isSelected ? selectedColor[preset.color] || selectedColor.slate : colorMap[preset.color] || colorMap.slate
        }`}
      >
        <span className="text-sm">{preset.icon}</span>
        <span className={`text-xs font-medium ${isSelected ? 'text-white' : 'text-zinc-300'}`}>
          {preset.name}
        </span>
        {isSelected && <Check className="w-3 h-3 text-green-400 ml-auto" />}
      </button>
    );
  }

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      onClick={() => onSelect(preset)}
      className={`relative flex flex-col items-start p-3 rounded-xl border transition-all text-left ${
        isSelected ? selectedColor[preset.color] || selectedColor.slate : colorMap[preset.color] || colorMap.slate
      }`}
    >
      <div className="flex items-center gap-2 w-full">
        <span className="text-lg">{preset.icon}</span>
        <span className={`text-sm font-medium flex-1 ${isSelected ? 'text-white' : 'text-zinc-200'}`}>
          {preset.name}
        </span>
        {preset.isUserPreset && onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(preset.id); }}
            className="text-zinc-500 hover:text-red-400 p-1"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
      <p className="text-[10px] text-zinc-500 mt-1 line-clamp-1">{preset.description}</p>
      {isSelected && (
        <div className="absolute top-2 right-2">
          <Check className="w-4 h-4 text-green-400" />
        </div>
      )}
    </motion.button>
  );
};

// Save preset modal
const SavePresetModal = ({ isOpen, onClose, onSave, currentSettings }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onSave(name.trim());
    setSaving(false);
    setName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
            <Save className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Save Preset</h3>
            <p className="text-xs text-zinc-500">Save current effect chain</p>
          </div>
        </div>

        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Preset name..."
          className="mb-4 bg-zinc-800 border-zinc-700"
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
        />

        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className="flex-1 bg-violet-600 hover:bg-violet-500"
          >
            {saving ? 'Saving...' : 'Save Preset'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Compact preset selector using portal to avoid overflow clipping
const CompactPresetSelector = ({ isOpen, setIsOpen, currentPreset, currentPresetId, handleSelectPreset, userPresets }) => {
  const triggerRef = useRef(null);
  
  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 hover:border-zinc-600 transition-colors"
      >
        <Zap className="w-3.5 h-3.5 text-amber-400" />
        <span className="text-xs text-zinc-300">
          {currentPreset?.name || 'Select Preset'}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <DropdownPortal
        triggerRef={triggerRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        width={256}
      >
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl p-2 max-h-64 overflow-y-auto">
          <div className="flex flex-wrap gap-1 mb-2">
            {FACTORY_PRESETS.slice(0, 6).map(preset => (
              <PresetCard
                key={preset.id}
                preset={preset}
                isSelected={currentPresetId === preset.id}
                onSelect={handleSelectPreset}
                compact
              />
            ))}
          </div>
          {userPresets.length > 0 && (
            <>
              <div className="text-[10px] text-zinc-500 uppercase mb-1 mt-2">My Presets</div>
              <div className="flex flex-wrap gap-1">
                {userPresets.map(preset => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    isSelected={currentPresetId === preset.id}
                    onSelect={handleSelectPreset}
                    compact
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </DropdownPortal>
    </div>
  );
};


/**
 * Main Preset Selector Component
 */
export const GuitarPresetSelector = ({
  trackId,
  currentPresetId,
  currentSettings,
  onSelectPreset,
  onSavePreset,
  userId,
  compact = false,
  showSaveButton = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [userPresets, setUserPresets] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch user presets
  useEffect(() => {
    const fetchUserPresets = async () => {
      if (!userId) return;
      try {
        const res = await fetch(`${API_URL}/api/guitar-presets?user_id=${userId}`);
        if (res.ok) {
          const data = await res.json();
          setUserPresets(data.presets || []);
        }
      } catch (error) {
        console.error('Failed to fetch user presets:', error);
      }
    };
    fetchUserPresets();
  }, [userId]);

  // Combined presets (factory + user)
  const allPresets = useMemo(() => {
    const factoryFiltered = selectedCategory === 'all' 
      ? FACTORY_PRESETS 
      : selectedCategory === 'custom'
        ? []
        : getPresetsByCategory(selectedCategory);
    
    const userFiltered = selectedCategory === 'all' || selectedCategory === 'custom'
      ? userPresets
      : [];

    const combined = [...factoryFiltered, ...userFiltered];

    if (searchQuery) {
      return combined.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return combined;
  }, [selectedCategory, searchQuery, userPresets]);

  // Get current preset name
  const currentPreset = useMemo(() => {
    if (!currentPresetId) return null;
    return [...FACTORY_PRESETS, ...userPresets].find(p => p.id === currentPresetId);
  }, [currentPresetId, userPresets]);

  // Handle preset selection
  const handleSelectPreset = useCallback((preset) => {
    const settings = applyPresetToTrack(preset);
    if (settings) {
      onSelectPreset(trackId, settings, preset.id);
      setIsOpen(false);
    }
  }, [trackId, onSelectPreset]);

  // Handle save preset
  const handleSavePreset = useCallback(async (name) => {
    if (!userId || !currentSettings) return;
    
    const newPreset = createUserPreset(name, currentSettings);
    
    try {
      const res = await fetch(`${API_URL}/api/guitar-presets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          preset: newPreset
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setUserPresets(prev => [...prev, data.preset || newPreset]);
        onSavePreset?.(newPreset);
      }
    } catch (error) {
      console.error('Failed to save preset:', error);
    }
  }, [userId, currentSettings, onSavePreset]);

  // Handle delete user preset
  const handleDeletePreset = useCallback(async (presetId) => {
    if (!userId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/guitar-presets/${presetId}?user_id=${userId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        setUserPresets(prev => prev.filter(p => p.id !== presetId));
      }
    } catch (error) {
      console.error('Failed to delete preset:', error);
    }
  }, [userId]);

  // Compact inline selector
  if (compact) {
    return (
      <CompactPresetSelector
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        currentPreset={currentPreset}
        currentPresetId={currentPresetId}
        handleSelectPreset={handleSelectPreset}
        userPresets={userPresets}
      />
    );
  }

  // Full preset browser
  return (
    <div className="space-y-3">
      {/* Header with current preset and actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800/50 border border-zinc-700 hover:border-zinc-600 transition-colors"
        >
          <Zap className="w-4 h-4 text-amber-400" />
          <span className="text-sm text-zinc-300 flex-1 text-left truncate">
            {currentPreset ? (
              <>
                <span className="mr-1">{currentPreset.icon}</span>
                {currentPreset.name}
              </>
            ) : (
              'Select Preset...'
            )}
          </span>
          <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {showSaveButton && currentSettings?.ampEnabled && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSaveModal(true)}
            className="px-2"
            title="Save current settings as preset"
          >
            <Save className="w-4 h-4 text-zinc-400" />
          </Button>
        )}
      </div>

      {/* Expanded preset browser */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-3 space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search presets..."
                  className="pl-9 h-8 text-sm bg-zinc-900/50 border-zinc-700"
                />
              </div>

              {/* Category tabs */}
              <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
                {[...PRESET_CATEGORIES, { id: 'custom', name: 'My Presets', icon: '💾' }].map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] whitespace-nowrap transition-colors ${
                      selectedCategory === cat.id
                        ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
                        : 'bg-zinc-800 text-zinc-500 hover:text-white border border-transparent'
                    }`}
                  >
                    <span>{cat.icon}</span>
                    <span>{cat.name}</span>
                  </button>
                ))}
              </div>

              {/* Preset grid */}
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {allPresets.map(preset => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    isSelected={currentPresetId === preset.id}
                    onSelect={handleSelectPreset}
                    onDelete={preset.isUserPreset ? handleDeletePreset : null}
                  />
                ))}
                {allPresets.length === 0 && (
                  <div className="col-span-2 py-6 text-center text-zinc-500 text-sm">
                    {selectedCategory === 'custom' && userPresets.length === 0
                      ? 'No saved presets yet. Create one!'
                      : 'No presets found'}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save preset modal */}
      <AnimatePresence>
        {showSaveModal && (
          <SavePresetModal
            isOpen={showSaveModal}
            onClose={() => setShowSaveModal(false)}
            onSave={handleSavePreset}
            currentSettings={currentSettings}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default GuitarPresetSelector;
